import React, { useEffect, useState } from "react";
import { Input, Upload, Flex } from "antd";
import { toast } from "react-toastify";
import { CameraOutlined } from "@ant-design/icons";
import { useAppDispatch } from "../../../../redux/utils/Dispatcher";
import { updateCurrentChat, addNewMessage } from "../../../../redux/slices/chatSlice";
import { formatSingleChat, formatMessage } from "../../../../utils";
import ENDPOINTS from "../../../../services/endpoint";
import apiInstance from "../../../../services/apiService";
import { storageService } from "../../../../services/storageService";
import ChatSidebarHeader from "../../../../components/common/ChatSidebarHeader";
import GradientButton from "../../../../components/common/UI/GradientButton";
import "./index.scss";

interface UpdateGroupProps {
  groupInfo: any;
  handleBackClick: () => void;
}

const UpdateGroup: React.FC<UpdateGroupProps> = ({
  handleBackClick,
  groupInfo,
}) => {
  const dispatch = useAppDispatch();
  const userId = storageService.getUserId();
  const [groupName, setGroupName] = useState<string>("");
  const [groupImage, setGroupImage] = useState<File | null>(null);
  const [groupImageURL, setGroupImageURL] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setGroupImageURL(e.target.result as string);
        setGroupImage(file);
      }
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleUpdateGroup = async () => {
    if (!groupName) {
      toast.error("Group name is required");
      return;
    }

    const URL = ENDPOINTS.GROUP_PERMISSION_UPDATE;
    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("name", groupName);
      formData.append("groupId", groupInfo.id);
      if (groupImage) {
        formData.append("file", groupImage);
      }

      const response = await apiInstance.post(URL, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        const updatedDetails = formatSingleChat(response.data.groupDetail);
        dispatch(
          updateCurrentChat({
            image: updatedDetails.image,
            name: updatedDetails.name,
          })
        );

        const newMsg = formatMessage(
          response?.data?.newMessage || response?.data?.conversation,
          userId
        );

        if (newMsg?.message) {
          dispatch(addNewMessage(newMsg));
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Error creating group. Please try again");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (groupInfo) {
      setGroupImageURL(groupInfo.image);
      setGroupName(groupInfo.name);
    }
  }, [groupInfo]);

  return (
    <div className="update-group-screen">
      <ChatSidebarHeader
        title="Update Group"
        handleBackClick={handleBackClick}
      />

      <div className="name-container">
        <Flex align="center" gap={10} className="w-full">
          <Upload
            className="image-upload"
            beforeUpload={(file) => handleImageUpload(file)}
            multiple={false}
            showUploadList={false}
            disabled={loading}
            accept="image/*"
          >
            {groupImageURL ? (
              <img src={groupImageURL} alt="Profile" />
            ) : (
              <CameraOutlined className="camera" />
            )}
          </Upload>

          <Input
            value={groupName}
            disabled={loading}
            onChange={(e) => setGroupName(e.target.value)}
            className="custom-input group-name-input"
            placeholder="Group name"
          />
        </Flex>

        <GradientButton
          loading={loading}
          onClick={handleUpdateGroup}
          className="update-btn"
        >
          Update
        </GradientButton>
      </div>
    </div>
  );
};

export default UpdateGroup;
